import React, { useState, useEffect, useCallback } from "react";
import { Stack, Pagination } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AcademicYearSelectFilter from "../components/common/AcademicYearSelectFilter";
import VoteHeadSelectFilter from "../components/common/VoteHeadSelectFilter";
import StudentSearchSelect from "../components/common/StudentSearchSelect";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import FeeAllocationCard from "../components/feeAllocations/FeeAllocationCard";
import CreateFeeAllocationModal from "../components/feeAllocations/CreateFeeAllocationModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getFeeAllocations,
  deleteFeeAllocation,
  resetFeeAllocationServerStatus,
} from "../redux/actions/feeAllocationActions";
import { StudentFeeAllocationResponseDto } from "../shared/dtos/studentFeeAllocation/StudentFeeAllocationResponseDto";

const FeeAllocationsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    feeAllocations,
    pagination,
    loadingFeeAllocations,
    errorLoadingFeeAllocations,
    serverStatus,
    loadingFeeAllocationCreate,
    errorCreatingFeeAllocation,
    loadingFeeAllocationDelete,
  } = useAppSelector((state) => state.feeAllocation);

  // Filter States
  const [page, setPage] = useState<number>(1);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedAcademicYearId, setSelectedAcademicYearId] =
    useState<string>("");
  const [selectedVoteHeadId, setSelectedVoteHeadId] = useState<string>("");
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedAllocation, setSelectedAllocation] =
    useState<StudentFeeAllocationResponseDto | null>(null);

  const canCreate = hasPermission("student_fee_allocation:create");
  const canDelete = hasPermission("student_fee_allocation:delete");

  const fetchAllocations = useCallback((): void => {
    dispatch(
      getFeeAllocations({
        page,
        size: 12,
        branchId: selectedBranchId || undefined,
        studentId: selectedStudentId || undefined,
        academicYearId: selectedAcademicYearId || undefined,
        voteHeadId: selectedVoteHeadId || undefined,
      }),
    );
  }, [
    dispatch,
    page,
    selectedBranchId,
    selectedStudentId,
    selectedAcademicYearId,
    selectedVoteHeadId,
  ]);

  useEffect(() => {
    fetchAllocations();
  }, [fetchAllocations]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingFeeAllocationCreate) setIsCreateModalOpen(false);
  }, [loadingFeeAllocationCreate]);

  const handleOpenDelete = (
    allocation: StudentFeeAllocationResponseDto,
  ): void => {
    setSelectedAllocation(allocation);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingFeeAllocationDelete) {
      setIsDeleteModalOpen(false);
      setSelectedAllocation(null);
    }
  }, [loadingFeeAllocationDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedAllocation) {
      const id =
        (selectedAllocation as any).id ??
        (selectedAllocation as any).allocationId;
      dispatch(deleteFeeAllocation(id));
    }
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetFeeAllocationServerStatus());
  }, [serverStatus, dispatch, handleCloseCreate, handleCloseDelete]);

  return (
    <PageContainer
      title="Fee Allocations"
      subtitle="Manage student optional fee subscriptions."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Allocate Fee
          </AppButton>
        )
      }
    >
      <PageToolbar hideSearch>
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPage(1);
          }}
        />

        <StudentSearchSelect
          value={selectedStudentId}
          branchId={selectedBranchId}
          onChange={(id) => {
            setSelectedStudentId(id);
            setPage(1);
          }}
        />

        <AcademicYearSelectFilter
          value={selectedAcademicYearId}
          onChange={(id) => {
            setSelectedAcademicYearId(id);
            setPage(1);
          }}
        />

        <VoteHeadSelectFilter
          value={selectedVoteHeadId}
          onChange={(id) => {
            setSelectedVoteHeadId(id);
            setPage(1);
          }}
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingFeeAllocations}
        error={errorLoadingFeeAllocations}
        itemCount={feeAllocations?.length || 0}
        emptyMessage="No Fee Allocations Found"
        onRetry={fetchAllocations}
      >
        {feeAllocations?.map((allocation) => (
          <FeeAllocationCard
            key={(allocation as any).id ?? (allocation as any).allocationId}
            allocation={allocation}
            onDelete={
              canDelete ? () => handleOpenDelete(allocation) : undefined
            }
          />
        ))}
      </CardGridContainer>

      {pagination?.totalPages && pagination.totalPages > 1 && (
        <Stack sx={{ alignItems: "center", mt: 4 }}>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={(_, val) => setPage(val)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateFeeAllocationModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingFeeAllocationCreate}
          error={errorCreatingFeeAllocation}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedAllocation && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Remove Allocation"
          description={`Are you sure you want to remove the ${selectedAllocation.voteHeadName} allocation for ${selectedAllocation.studentName}?`}
          loading={loadingFeeAllocationDelete}
        />
      )}
    </PageContainer>
  );
};

export default FeeAllocationsPage;
